import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { AppUser } from '../types';
import { isCommercialProfile } from '../utils/commercialRoleHelper';
import { fetchPropertyCommentCounts, updateLocalCommentCountCache } from '../services/propertyCommentsSummaryService';

export const usePropertyCommentCounts = (
  propertyCodes: string[],
  appUser: AppUser | null
) => {
  const isCommercial = isCommercialProfile(appUser?.role);
  const [counts, setCounts] = useState<Record<string, number>>({});

  // Compute a stable sorted string representation of codes to avoid object reference churn
  const codesKey = useMemo(() => {
    return Array.from(new Set((propertyCodes || []).filter(Boolean).map(String)))
      .sort()
      .join(',');
  }, [propertyCodes]);

  const loadCounts = useCallback(async (force = false) => {
    if (!isCommercial || !codesKey) {
      if (!isCommercial) setCounts({});
      return;
    }
    const codes = codesKey.split(',');
    const result = await fetchPropertyCommentCounts(codes, appUser, force);
    setCounts((prev) => {
      // Only trigger state update if there are actual new/different values
      let changed = false;
      for (const [code, val] of Object.entries(result)) {
        if (prev[code] !== val) {
          changed = true;
          break;
        }
      }
      return changed ? { ...prev, ...result } : prev;
    });
  }, [codesKey, appUser?.id, appUser?.role, isCommercial]);

  const prevKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (prevKeyRef.current !== codesKey) {
      prevKeyRef.current = codesKey;
      loadCounts(false);
    }
  }, [codesKey, loadCounts]);

  // Listen to comment modifications to update counts real-time
  useEffect(() => {
    const handleCommentUpdate = (event: Event) => {
      const customEvent = event as CustomEvent<{ propertyCode: string; delta?: number }>;
      const updatedCode = customEvent.detail?.propertyCode;
      if (updatedCode) {
        const delta = customEvent.detail?.delta ?? 0;
        updateLocalCommentCountCache(updatedCode, delta);
        setCounts((prev) => {
          const current = prev[updatedCode] || 0;
          const next = Math.max(0, current + delta);
          return { ...prev, [updatedCode]: next };
        });
      } else {
        loadCounts(true);
      }
    };

    window.addEventListener('property-comment-updated', handleCommentUpdate);
    return () => {
      window.removeEventListener('property-comment-updated', handleCommentUpdate);
    };
  }, [loadCounts]);

  const hasComments = useCallback(
    (code: string) => isCommercial && (counts[code] || 0) > 0,
    [counts, isCommercial]
  );

  const getCount = useCallback(
    (code: string) => (isCommercial ? counts[code] || 0 : 0),
    [counts, isCommercial]
  );

  return { counts, hasComments, getCount, refreshCounts: () => loadCounts(true) };
};
